import { useEffect, useState } from "react";
import { FileText, Plus, X, Warning } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { api } from "../lib/api";
import { formatET } from "../lib/dateUtils";
import { toast } from "sonner";

const navItems = [{ to: "/admin/signed-agreements", label: "Signed Agreements", icon: <FileText size={18} weight="duotone" />, testId: "nav-admin-signed-agreements" }];

const BLANK_FORM = { full_legal_name: "", email: "", phone: "", referral_code: "", agreement_name: "IGC Affiliate Starter Agreement", agreement_ref: "" };

export default function AdminSignedAgreementsPage() {
  const [agreements, setAgreements] = useState([]);
  const [webhookLog, setWebhookLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const loadAgreements = () => api.get("/admin/signed-agreements").then(({ data }) => setAgreements(data));
  const loadWebhookLog = () => api.get("/admin/signed-agreements/webhook-log").then(({ data }) => setWebhookLog(data));

  useEffect(() => {
    Promise.all([loadAgreements(), loadWebhookLog()]).finally(() => setLoading(false));
  }, []);

  const recentFailures = webhookLog.filter((w) => w.outcome === "rejected_bad_secret" || w.outcome === "rejected_bad_json" || w.outcome === "error");

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_legal_name.trim()) {
      toast.error("Full legal name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/signed-agreements/manual", form);
      toast.success("Agreement added manually");
      setForm(BLANK_FORM);
      setShowManualForm(false);
      loadAgreements();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add agreement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Signed Agreements" moduleBackPath="/admin">
      <div data-testid="admin-signed-agreements-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight">Signed Agreements</h1>
          <div className="flex items-center gap-2">
            <button
              data-testid="webhook-log-toggle-button"
              onClick={() => {
                setShowLog((v) => !v);
                loadWebhookLog();
              }}
              title="Every call our server received from the Netlify webhook — use this to check whether a signed agreement actually reached us, or never fired."
              className="text-xs px-3 py-2 border border-igc-gold/20 text-igc-muted hover:text-igc-cream hover:border-igc-gold/40 transition-colors flex items-center gap-1.5"
            >
              <Warning size={14} weight="duotone" className={recentFailures.length > 0 ? "text-igc-danger" : ""} />
              Webhook Log {recentFailures.length > 0 && <span className="text-igc-danger">({recentFailures.length} failed)</span>}
            </button>
            <button
              data-testid="add-agreement-manually-button"
              onClick={() => setShowManualForm((v) => !v)}
              title="If a client signed but their record never auto-populated, add it here by hand — same result as the webhook firing."
              className="text-xs px-3 py-2 bg-igc-gold text-igc-charcoal font-medium hover:bg-igc-gold/90 transition-colors flex items-center gap-1.5"
            >
              <Plus size={14} weight="bold" /> Add Manually
            </button>
          </div>
        </div>
        <p className="text-igc-muted text-sm mb-6" title="Every onboarding agreement a client digitally signs on agreements.igchq.com lands here automatically — no need to hunt through emails.">
          Every signed onboarding agreement from agreements.igchq.com — populated automatically the moment a client signs, no need to check email.
        </p>

        {showManualForm && (
          <form onSubmit={handleManualSubmit} data-testid="manual-agreement-form" className="bg-igc-surface border border-igc-gold/20 p-5 mb-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-igc-cream text-sm font-medium">Add Signed Agreement Manually</p>
              <button type="button" data-testid="close-manual-form-button" onClick={() => setShowManualForm(false)} className="text-igc-muted hover:text-igc-cream"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="manual-agreement-name-input" required placeholder="Full legal name *" value={form.full_legal_name} onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
              <input data-testid="manual-agreement-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
              <input data-testid="manual-agreement-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
              <input data-testid="manual-agreement-refcode-input" placeholder="Referring rep code (e.g. GH001)" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
              <input data-testid="manual-agreement-name-field-input" placeholder="Agreement name" value={form.agreement_name} onChange={(e) => setForm({ ...form, agreement_name: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
              <input data-testid="manual-agreement-ref-input" placeholder="Agreement ref (optional)" value={form.agreement_ref} onChange={(e) => setForm({ ...form, agreement_ref: e.target.value })} className="bg-igc-charcoal border border-igc-gold/15 px-3 py-2 text-sm text-igc-cream" />
            </div>
            <button data-testid="manual-agreement-submit-button" disabled={saving} type="submit" className="text-xs px-4 py-2 bg-igc-gold text-igc-charcoal font-medium hover:bg-igc-gold/90 transition-colors disabled:opacity-50">
              {saving ? "Saving..." : "Save Agreement"}
            </button>
          </form>
        )}

        {showLog && (
          <div data-testid="webhook-log-panel" className="bg-igc-surface border border-igc-gold/20 p-5 mb-6">
            <p className="text-igc-cream text-sm font-medium mb-3">Netlify Webhook Log — last 100 calls received</p>
            {webhookLog.length === 0 && <p className="text-igc-muted text-xs" data-testid="webhook-log-empty">No webhook calls have been received yet.</p>}
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {webhookLog.map((w) => (
                <div key={w.id} data-testid={`webhook-log-entry-${w.id}`} className="flex items-center justify-between gap-3 text-xs border-b border-igc-gold/5 pb-1.5">
                  <span className={w.outcome === "success" ? "text-igc-success" : "text-igc-danger"}>{w.outcome}{w.signer_name ? ` — ${w.signer_name}` : ""}{w.error ? ` (${w.error})` : ""}</span>
                  <span className="text-igc-muted whitespace-nowrap">{formatET(w.received_at, "MMM d, h:mm:ss a")} ET</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && agreements.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="admin-signed-agreements-empty">
            No agreements have been signed yet — they'll appear here the instant a client signs one.
          </p>
        )}

        <div className="space-y-2">
          {agreements.map((a) => (
            <div key={a.id} className="bg-igc-surface border border-igc-gold/15 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`signed-agreement-${a.id}`}>
              <div>
                <p className="text-igc-cream text-sm flex items-center gap-2">
                  {a.full_legal_name || "Unknown signer"}
                  <span className="text-[10px] px-2 py-0.5 bg-igc-success/10 text-igc-success uppercase tracking-wide" data-testid={`signed-agreement-status-${a.id}`}>{a.signature_status}</span>
                  {a.manually_added && <span className="text-[10px] px-2 py-0.5 bg-igc-gold/10 text-igc-gold uppercase tracking-wide" data-testid={`signed-agreement-manual-badge-${a.id}`} title="Added by hand after a webhook miss, not via the automatic Netlify webhook">Manual</span>}
                </p>
                <p className="text-igc-muted text-xs mt-1">{a.email || "no email"} — {a.phone || "no phone"}</p>
                <p className="text-igc-muted text-xs">{a.agreement_name || "Onboarding Agreement"} ({a.agreement_ref || "no ref"})</p>
                {a.rep_display_name && <p className="text-igc-muted text-xs">Referred by {a.rep_display_name} ({a.rep_code})</p>}
              </div>
              <p className="text-igc-muted text-xs" data-testid={`signed-agreement-date-${a.id}`}>Signed {formatET(a.received_at, "MMM d, yyyy 'at' h:mm a")} ET</p>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
